import { Outlet, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../providers/AuthProvider';
import { useDirection, useLanguage } from '../../lib/i18n/hooks';
import { LanguageSwitcher } from '../../components/i18n/LanguageSwitcher';
import {
  LayoutDashboard,
  BookOpen,
  Video,
  Film,
  Users,
  Calendar,
  GraduationCap,
  Settings,
  Sparkles,
  LogOut,
  Menu,
  X,
  DollarSign,
  Megaphone,
  School,
  Bell,
  MessageSquare,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Plus,
  FolderOpen,
  Clock,
  UserPlus,
  Grid3X3,
} from 'lucide-react';
import { useState, useEffect } from 'react';

// Navigation items matching Figma design order
const navItems = [
  { path: '/admin', icon: LayoutDashboard, labelKey: 'nav.dashboard', label: 'Dashboard' },
  { path: '/admin/school', icon: School, labelKey: 'nav.mySchool', label: 'My School' },
  { path: '/admin/academics', icon: GraduationCap, labelKey: 'nav.academics', label: 'Academics' },
  { path: '/admin/courses', icon: BookOpen, labelKey: 'nav.courses', label: 'Courses' },
  { path: '/admin/meetings', icon: Video, labelKey: 'nav.meetings', label: 'Meetings' },
  { path: '/admin/recordings', icon: Film, labelKey: 'nav.recordings', label: 'Recordings' },
  { path: '/admin/library', icon: FolderOpen, labelKey: 'nav.library', label: 'Library' },
  { path: '/admin/finance', icon: DollarSign, labelKey: 'nav.finance', label: 'Finance' },
  { path: '/admin/marketing', icon: Megaphone, labelKey: 'nav.marketing', label: 'Marketing' },
  { path: '/admin/ai-teacher', icon: Sparkles, labelKey: 'nav.aiTeacher', label: 'AI Teacher' },
];

// Mini Calendar Component
const MiniCalendar: React.FC<{
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  isRTL?: boolean;
  onNavigateToSchedule?: () => void;
}> = ({ selectedDate, onSelectDate, isRTL = false, onNavigateToSchedule }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = isRTL
    ? ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = isRTL ? ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const today = new Date();

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  // Format date header like "Mon, Aug 17"
  const formatSelectedDate = () => {
    const days = isRTL
      ? ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = isRTL
      ? ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ', 'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[selectedDate.getDay()]}, ${months[selectedDate.getMonth()]} ${selectedDate.getDate()}`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Selected Date Header - Clickable to open schedule */}
      <div
        onClick={onNavigateToSchedule}
        className="px-3 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <p className="text-sm font-medium text-gray-900">{formatSelectedDate()}</p>
        <p className="text-xs text-teal-600">View Full Schedule →</p>
      </div>

      <div className="p-3">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-900">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <div className="flex gap-1">
            <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft size={16} className="text-gray-500" />
            </button>
            <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
              <ChevronRight size={16} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((day, i) => (
            <div key={i} className="text-center text-xs text-gray-400 font-medium py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 42 }, (_, i) => {
            const day = i - firstDay + 1;
            const isValidDay = day > 0 && day <= daysInMonth;
            const isToday = isValidDay &&
              day === today.getDate() &&
              currentMonth.getMonth() === today.getMonth() &&
              currentMonth.getFullYear() === today.getFullYear();
            const isSelected = isValidDay &&
              day === selectedDate.getDate() &&
              currentMonth.getMonth() === selectedDate.getMonth() &&
              currentMonth.getFullYear() === selectedDate.getFullYear();

            return (
              <button
                key={i}
                onClick={() => isValidDay && onSelectDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
                disabled={!isValidDay}
                className={`
                  text-xs py-1.5 rounded-full transition-colors
                  ${!isValidDay ? 'text-gray-300 cursor-default' : 'hover:bg-gray-100'}
                  ${isToday ? 'bg-teal-600 text-white hover:bg-teal-700' : ''}
                  ${isSelected && !isToday ? 'bg-teal-100 text-teal-700' : ''}
                `}
              >
                {isValidDay ? day : ''}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Header Stat Card Component (matches Figma design)
const HeaderStatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: { label: string; onClick: () => void };
  className?: string;
}> = ({ title, value, subtitle, icon, action, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200 p-4 min-w-[140px] ${className}`}>
    <div className="flex items-start justify-between mb-2">
      <div>
        <p className="text-xs text-gray-500 font-medium">{title}</p>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
      {icon && (
        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
          {icon}
        </div>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {action && (
      <button
        onClick={action.onClick}
        className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium"
      >
        + {action.label}
      </button>
    )}
  </div>
);

export function DashboardLayout() {
  const { t } = useTranslation();
  const { user, logout, currentSchool } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isRTL, direction } = useDirection();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [notificationCount] = useState(2);
  const [messageCount] = useState(3);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mock notifications - in real app would come from API
  const notifications = [
    { id: 1, title: 'New student enrolled', message: 'John Doe enrolled in Math 101', time: '5 min ago', read: false },
    { id: 2, title: 'Meeting reminder', message: 'Staff meeting in 30 minutes', time: '25 min ago', read: false },
  ];

  // Mock stats - in real app would come from API
  const stats = {
    students: 0,
    teachers: 0,
    courses: 0,
  };

  return (
    <div className={`flex h-screen bg-gray-100 ${isRTL ? 'rtl' : 'ltr'}`} dir={direction}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 start-0 z-50 bg-slate-800 transition-all duration-300 lg:relative lg:!translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        } ${sidebarCollapsed ? 'w-20' : 'w-72'}`}
      >
        <div className="flex h-full flex-col">
          {/* Logo Section */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white">
                <GraduationCap className="h-6 w-6" />
              </div>
              {!sidebarCollapsed && (
                <span className="text-xl font-bold text-white">VIScool</span>
              )}
            </Link>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:block p-1.5 hover:bg-slate-700 rounded-lg text-slate-400"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-slate-700 rounded-lg text-slate-400"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* User Profile Card */}
          <div className={`p-4 ${sidebarCollapsed ? 'px-2' : ''}`}>
            <NavLink
              to="/admin/profile"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors ${
                sidebarCollapsed ? 'justify-center p-2' : ''
              }`}
            >
              <div className="relative flex-shrink-0">
                <div className={`rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold ring-2 ring-teal-500/50 ${
                  sidebarCollapsed ? 'w-10 h-10 text-base' : 'w-12 h-12 text-lg'
                }`}>
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-800 rounded-full" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white truncate">{user?.full_name || 'User Name'}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </div>
              )}
            </NavLink>

            {/* Quick Action Icons - Below Profile (Figma design) */}
            {!sidebarCollapsed && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <button
                  onClick={() => navigate('/admin/messages')}
                  className="relative p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <MessageSquare className="h-5 w-5 text-slate-400" />
                  {messageCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-[10px] rounded-full flex items-center justify-center">
                      {messageCount}
                    </span>
                  )}
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <Bell className="h-5 w-5 text-slate-400" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                        {notificationCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                      <div className="absolute start-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
                        <div className="p-4 border-b border-gray-100">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">Notifications</h3>
                            <button className="text-xs text-teal-600 hover:text-teal-700">Mark all read</button>
                          </div>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.length > 0 ? (
                            notifications.map(notif => (
                              <div
                                key={notif.id}
                                className={`p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${!notif.read ? 'bg-teal-50/50' : ''}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-2 h-2 rounded-full mt-2 ${!notif.read ? 'bg-teal-500' : 'bg-transparent'}`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{notif.message}</p>
                                    <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-8 text-center">
                              <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No notifications</p>
                            </div>
                          )}
                        </div>
                        <div className="p-3 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setShowNotifications(false);
                              navigate('/admin/notifications');
                            }}
                            className="w-full text-center text-sm text-teal-600 hover:text-teal-700 font-medium"
                          >
                            View All Notifications
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => navigate('/admin/settings')}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Settings className="h-5 w-5 text-slate-400" />
                </button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 space-y-1 px-3 overflow-y-auto ${sidebarCollapsed ? 'px-2' : ''}`}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/admin' && location.pathname.startsWith(item.path));
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/admin'}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    sidebarCollapsed ? 'justify-center' : ''
                  } ${
                    isActive
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>

          {/* Mini Calendar (only when not collapsed) */}
          {!sidebarCollapsed && (
            <div className="px-3 pb-3">
              <MiniCalendar
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                isRTL={isRTL}
                onNavigateToSchedule={() => navigate('/admin/schedule')}
              />
            </div>
          )}

          {/* Settings & Logout */}
          <div className={`border-t border-slate-700 p-3 space-y-1 ${sidebarCollapsed ? 'px-2' : ''}`}>
            <NavLink
              to="/admin/settings"
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
            >
              <Settings className="h-5 w-5" />
              {!sidebarCollapsed && <span>{t('nav.settings', 'Settings')}</span>}
            </NavLink>
            <button
              onClick={logout}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/20 transition-colors w-full ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
            >
              <LogOut className="h-5 w-5" />
              {!sidebarCollapsed && <span>{t('common.logout', 'Logout')}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top Header with Stats (Figma design) */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* School Info Card */}
            <div className="hidden md:flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-2 min-w-[180px]">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center text-teal-600">
                <School className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{currentSchool?.name || 'School Name'}</p>
                <p className="text-xs text-gray-500">Country</p>
              </div>
              <button className="text-xs text-teal-600 hover:underline ms-auto">
                Edit School Details
              </button>
            </div>

            {/* Stats Cards */}
            <div className="hidden lg:flex items-center gap-3 flex-1">
              <HeaderStatCard
                title="My Students"
                value={stats.students}
                action={{ label: 'Add Student', onClick: () => navigate('/admin/school?tab=students') }}
              />
              <HeaderStatCard
                title="My Teachers"
                value={stats.teachers}
                action={{ label: 'Add Teacher', onClick: () => navigate('/admin/school?tab=teachers') }}
              />
              <HeaderStatCard
                title="Courses"
                value={stats.courses}
                icon={<BookOpen className="h-4 w-4 text-teal-600" />}
                action={{ label: 'Add Course', onClick: () => navigate('/admin/courses') }}
              />
            </div>

            {/* Right Section: Language Switcher & Add Widgets */}
            <div className="flex items-center gap-2 ms-auto">
              <LanguageSwitcher variant="compact" className="hidden sm:block" />

              <button className="flex items-center gap-2 px-3 py-2 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100 transition-colors">
                <Grid3X3 className="h-4 w-4" />
                <span className="text-sm font-medium hidden md:inline">Add Widgets</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
